import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, Play, RotateCcw, CheckCircle2, Clock, Target } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Trail Making A: 1-25 numbers
// Trail Making B: 1-A-2-B-3-C-...-13
const generateNodes = (type) => {
  const nodes = [];
  if (type === 'A') {
    for (let i = 1; i <= 25; i++) {
      nodes.push({ id: i, label: String(i), order: i - 1 });
    }
  } else {
    // B: 1-A-2-B-3-C-4-D-5-E-6-F-7-G-8-H-9-I-10-J-11-K-12-L-13
    const letters = 'ABCDEFGHİJKL';
    let order = 0;
    for (let i = 0; i < 13; i++) {
      nodes.push({ id: `n${i + 1}`, label: String(i + 1), order: order++ });
      if (i < 12) {
        nodes.push({ id: `l${i}`, label: letters[i], order: order++ });
      }
    }
  }
  // Pseudo-random but deterministic positions within grid
  const positions = [];
  const cols = 5;
  const rows = Math.ceil(nodes.length / cols);
  for (let i = 0; i < nodes.length; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    const x = (col + 0.5) / cols * 100;
    const y = (row + 0.5) / rows * 100;
    positions.push({ x: x + (Math.sin(i * 7.3) * 8), y: y + (Math.cos(i * 5.1) * 8) });
  }
  // Shuffle positions
  const shuffled = [...positions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return nodes.map((n, i) => ({
    ...n,
    x: Math.max(5, Math.min(95, shuffled[i].x)),
    y: Math.max(5, Math.min(95, shuffled[i].y)),
  }));
};

export default function TrailMakingTask({ onBack }) {
  const [phase, setPhase] = useState('intro');
  const [taskType, setTaskType] = useState('A');
  const [nodes, setNodes] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(0);
  const [clickedNodes, setClickedNodes] = useState([]);
  const [errors, setErrors] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [result, setResult] = useState(null);
  const [showError, setShowError] = useState(false);
  const errorTimeout = useRef(null);

  const startGame = useCallback((type) => {
    setTaskType(type);
    const generated = generateNodes(type);
    setNodes(generated);
    setCurrentOrder(0);
    setClickedNodes([]);
    setErrors(0);
    setStartTime(Date.now());
    setResult(null);
    setShowError(false);
    setPhase('playing');
  }, []);

  const handleNodeClick = useCallback((node) => {
    if (node.order === currentOrder) {
      const updated = [...clickedNodes, node.id];
      setClickedNodes(updated);
      const next = currentOrder + 1;
      setCurrentOrder(next);
      if (next >= nodes.length) {
        submitResults(updated.length, errors, (Date.now() - startTime) / 1000);
      }
    } else {
      setErrors(prev => prev + 1);
      setShowError(true);
      if (errorTimeout.current) clearTimeout(errorTimeout.current);
      errorTimeout.current = setTimeout(() => setShowError(false), 600);
    }
  }, [currentOrder, clickedNodes, nodes, errors, startTime]);

  useEffect(() => {
    return () => { if (errorTimeout.current) clearTimeout(errorTimeout.current); };
  }, []);

  const submitResults = async (total, errs, timeSec) => {
    const payload = {
      task_type: taskType,
      total_nodes: total,
      completed: true,
      time_seconds: timeSec,
      errors: errs,
    };
    try {
      const res = await axios.post(`${API}/api/cognitive/trail-making/submit`, payload);
      setResult(res.data);
      setPhase('result');
    } catch {
      setResult({ label: 'Hata', description: 'Sonuç kaydedilemedi.', color: 'red', time_seconds: timeSec, errors: errs });
      setPhase('result');
    }
  };

  const colorMap = { emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' }, cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' }, amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' }, orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30' }, red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' } };

  // INTRO
  if (phase === 'intro') {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-lg w-full" data-testid="trail-intro">
          <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white/80 mb-6 transition" data-testid="trail-back">
            <ArrowLeft className="w-4 h-4" /> Geri Dön
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
              <Target className="w-6 h-6 text-teal-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Trail Making Görevi</h1>
              <p className="text-white/50 text-sm">Görsel Tarama ve Bilişsel Esneklik</p>
            </div>
          </div>
          <div className="space-y-3 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-1">Bölüm A — Sayıları Bağlama</h3>
              <p className="text-white/50 text-xs">Daireleri sırayla (1 → 2 → 3 → ... → 25) tıklayın. Mümkün olduğunca hızlı ve hatasız olun.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-white font-semibold text-sm mb-1">Bölüm B — Sayı ve Harf Değişimi</h3>
              <p className="text-white/50 text-xs">Sayı ve harfleri sırayla değiştirerek tıklayın (1 → A → 2 → B → 3 → C → ...). Bilişsel esneklik ölçer.</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/50 mb-6">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> ~3 dakika</span>
          </div>
          <div className="flex gap-3">
            <button onClick={() => startGame('A')} className="flex-1 py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2" data-testid="trail-start-a">
              <Play className="w-4 h-4" /> Bölüm A
            </button>
            <button onClick={() => startGame('B')} className="flex-1 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2" data-testid="trail-start-b">
              <Play className="w-4 h-4" /> Bölüm B
            </button>
          </div>
        </div>
      </div>
    );
  }

  // PLAYING
  if (phase === 'playing') {
    const progress = (currentOrder / nodes.length) * 100;
    const elapsed = startTime ? ((Date.now() - startTime) / 1000).toFixed(0) : 0;
    const nextLabel = nodes.find(n => n.order === currentOrder)?.label || '';

    return (
      <div className="min-h-screen bg-[#0f172a] p-4" data-testid="trail-game">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3 text-sm text-white/50">
            <span>Trail Making — Bölüm {taskType}</span>
            <span className="flex items-center gap-3">
              <span>Hata: <span className="text-red-400">{errors}</span></span>
              <span><Clock className="w-3 h-3 inline" /> {elapsed}s</span>
            </span>
          </div>
          <div className="w-full h-1.5 bg-white/10 rounded-full mb-3">
            <div className="h-full bg-teal-500 rounded-full transition-all duration-200" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-center text-white/40 text-xs mb-3">
            Sıradaki: <span className="text-cyan-300 font-bold text-sm">{nextLabel}</span>
          </div>

          <div className={`relative bg-white/5 border rounded-2xl overflow-hidden transition-all ${showError ? 'border-red-500/50' : 'border-white/10'}`}
               style={{ paddingBottom: '80%' }}>
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
              {clickedNodes.length >= 2 && clickedNodes.map((id, i) => {
                if (i === 0) return null;
                const from = nodes.find(n => n.id === clickedNodes[i - 1]);
                const to = nodes.find(n => n.id === id);
                if (!from || !to) return null;
                return (
                  <line key={`line-${i}`}
                    x1={`${from.x}%`} y1={`${from.y}%`}
                    x2={`${to.x}%`} y2={`${to.y}%`}
                    stroke="rgba(45,212,191,0.3)" strokeWidth="2" />
                );
              })}
            </svg>
            {nodes.map(node => {
              const isClicked = clickedNodes.includes(node.id);
              const isCurrent = node.order === currentOrder;
              const isNumber = !isNaN(node.label);
              return (
                <button key={node.id}
                  onClick={() => !isClicked && handleNodeClick(node)}
                  disabled={isClicked}
                  className={`absolute w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all z-10 border
                    ${isClicked
                      ? 'bg-teal-500/30 border-teal-500/50 text-teal-300 scale-90'
                      : isCurrent
                        ? 'bg-white/15 border-cyan-400/60 text-white animate-pulse shadow-lg shadow-cyan-500/20'
                        : `bg-white/5 border-white/20 ${isNumber ? 'text-white/70' : 'text-amber-300/70'} hover:bg-white/10 hover:border-white/40`
                    }`}
                  style={{ left: `calc(${node.x}% - 18px)`, top: `calc(${node.y}% - 18px)` }}
                  data-testid={`trail-node-${node.label}`}>
                  {node.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // RESULT
  if (phase === 'result' && result) {
    const c = colorMap[result.color] || colorMap.amber;
    return (
      <div className="min-h-screen bg-[#0f172a] py-8 px-4" data-testid="trail-result">
        <div className="max-w-lg mx-auto space-y-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <div className="text-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-teal-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white">Trail Making — Bölüm {taskType}</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`${c.bg} border ${c.border} rounded-xl p-4 text-center`}>
                <p className={`text-2xl font-bold ${c.text}`}>{result.time_seconds?.toFixed(1)}s</p>
                <p className="text-white/40 text-xs mt-1">Süre</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{result.errors}</p>
                <p className="text-white/40 text-xs mt-1">Hata</p>
              </div>
            </div>
            {result.effective_time_seconds && (
              <p className="text-white/40 text-xs text-center mb-4">Etkin süre (hata cezalı): {result.effective_time_seconds?.toFixed(1)}s</p>
            )}
            <div className="text-center mb-4">
              <span className={`${c.bg} ${c.text} px-4 py-1.5 rounded-full text-sm font-medium border ${c.border}`}>
                {result.label}
              </span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed text-center">{result.description}</p>
            {result.measures && <p className="text-white/30 text-xs text-center mt-3">Ölçülen: {result.measures}</p>}
          </div>
          <div className="flex gap-3">
            <button onClick={() => startGame(taskType)} className="flex-1 py-3 bg-teal-500/20 text-teal-300 rounded-xl font-medium transition hover:bg-teal-500/30 flex items-center justify-center gap-2" data-testid="trail-retry">
              <RotateCcw className="w-4 h-4" /> Tekrarla
            </button>
            <button onClick={() => setPhase('intro')} className="flex-1 py-3 bg-white/10 text-white/70 rounded-xl font-medium transition hover:bg-white/15" data-testid="trail-switch">
              Diğer Bölüm
            </button>
            <button onClick={onBack} className="flex-1 py-3 bg-white/10 text-white/70 rounded-xl font-medium transition hover:bg-white/15" data-testid="trail-done">
              Tamamla
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
